import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CustomAutocomplete from 'components/shared/CustomAutocomplete/CustomAutocomplete';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomTextField from 'components/shared/CustomTextField/CustomTextField';
import { useOwnerOptions } from 'components/shared/hooks/useOwnerOptions';
import { useImagePreview } from 'customHook/useProfilePicture';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { HuddleClientService } from 'service/huddle-client.service';
import { LocationService } from 'service/location.service';
import zeplinColor from 'theme/Color';

const MenuProps = {
  PaperProps: { sx: { borderRadius: '14px' } },
};

export type ClientFormValues = {
  clientType: string;
  salutation: string;
  name: string;
  linkEntity: string[];
  agency: string;
  owner: string;
  league: string;
  team: string;
  position: string;
  number: string;
  status: string;
  college: string;
  loanOutCompany: string;
  mobile: string;
  phone: string;
  email: string;
  personalEmail: string;
  profileImageUrl: string;
  // Billing address
  billingStreet: string;
  billingCity: string;
  billingState: string;
  billingZip: string;
  billingCountry: string;
  // Mailing address
  mailingStreet: string;
  mailingCity: string;
  mailingState: string;
  mailingZip: string;
  mailingCountry: string;
  // Bio
  bio: string;
};

export type ClientFormHandle = {
  uploadImage: () => Promise<string>;
};

export type ClientFormProps = {
  /** Initial values — used for pre-populating on edit */
  initialValues?: Partial<ClientFormValues>;
  /** Called on every change with the latest form state */
  onChange?: (values: ClientFormValues, isValid: boolean) => void;
};

const EMPTY: ClientFormValues = {
  clientType: '',
  salutation: '',
  name: '',
  linkEntity: [],
  agency: '',
  owner: '',
  league: '',
  team: '',
  position: '',
  number: '',
  status: '',
  college: '',
  loanOutCompany: '',
  mobile: '',
  phone: '',
  email: '',
  personalEmail: '',
  profileImageUrl: '',
  billingStreet: '',
  billingCity: '',
  billingState: '',
  billingZip: '',
  billingCountry: '',
  mailingStreet: '',
  mailingCity: '',
  mailingState: '',
  mailingZip: '',
  mailingCountry: '',
  bio: '',
};

const sectionCard = {
  padding: '20px',
  borderRadius: '14px',
  gap: '16px',
  backgroundColor: zeplinColor.Neutral2,
  display: 'flex',
  flexDirection: 'column',
} as const;

const ClientForm = forwardRef<ClientFormHandle, ClientFormProps>(
  ({ initialValues, onChange }, ref) => {
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('xl'));

    const [formValues, setFormValues] = useState<ClientFormValues>({ ...EMPTY, ...initialValues });

    // Dropdown options
    const [clientTypeOptions, setClientTypeOptions] = useState<string[]>([]);
    const { ownerOptions } = useOwnerOptions();
    const [dropdowns, setDropdowns] = useState<{
      salutation: string[];
      league: string[];
      team: string[];
      position: string[];
      status: string[];
      college: string[];
      agency: { Id: string; Name: string }[];
      linkEntity: { Id: string; Name: string; clientType: string }[];
    }>({
      salutation: [],
      league: [],
      team: [],
      position: [],
      status: [],
      college: [],
      agency: [],
      linkEntity: [],
    });

    // Agency custom search
    const waitRef = useRef<any>(null);
    const [agencyOptions, setAgencyOptions] = useState<{ Id: string; Name: string }[]>([]);
    const [agencyLoading, setAgencyLoading] = useState(false);
    const [selectedAgency, setSelectedAgency] = useState<{ Id: string; Name: string } | null>(null);
    const [selectedOwner, setSelectedOwner] = useState<{ Id: string; Name: string } | null>(null);

    // Billing address search
    const billingWaitRef = useRef<any>(null);
    const [billingAddressOptions, setBillingAddressOptions] = useState<
      { Id: string; Name: string }[]
    >([]);
    const [billingAddressLoading, setBillingAddressLoading] = useState(false);
    const [selectedBillingAddress, setSelectedBillingAddress] = useState<{
      Id: string;
      Name: string;
    } | null>(null);

    // Mailing address search
    const mailingWaitRef = useRef<any>(null);
    const [mailingAddressOptions, setMailingAddressOptions] = useState<
      { Id: string; Name: string }[]
    >([]);
    const [mailingAddressLoading, setMailingAddressLoading] = useState(false);
    const [selectedMailingAddress, setSelectedMailingAddress] = useState<{
      Id: string;
      Name: string;
    } | null>(null);

    // Billing — same as mailing toggle
    const [sameAsMailing, setSameAsMailing] = useState(false);

    // Profile picture
    const {
      fileInputRef,
      previewUrl: avatarPreview,
      handleFileChange,
      clearSelection,
      openFileSelection,
      uploadProfilePicture,
    } = useImagePreview(initialValues?.profileImageUrl ?? null);

    useImperativeHandle(ref, () => ({
      uploadImage: async (): Promise<string> => {
        if (avatarPreview && avatarPreview !== initialValues?.profileImageUrl) {
          const result = await uploadProfilePicture({ key: false, type: 'client' });
          return result ?? '';
        }
        // Keep existing image on edit, or empty on create
        return initialValues?.profileImageUrl ?? '';
      },
    }));

    // ── Fetch client types ──────────────────────────────────────────────────────
    useEffect(() => {
      HuddleClientService.getClientTypesV2()
        .then((res) => {
          const names = res?.types?.map((t) => t?.name);
          setClientTypeOptions(
            names?.map((n) => n?.toLocaleLowerCase()).filter((t) => t !== 'all') ?? [],
          );
        })
        .catch(() => {});
    }, []);

    // ── Fetch dropdowns once client type is selected ────────────────────────────
    useEffect(() => {
      if (!formValues.clientType) return;
      HuddleClientService.getHuddleClientDropdowns('Talent')
        .then((res) => {
          const agencyOptionsFromDropdown = (res?.agency ?? [])
            .map((item: any) => ({ Id: item?.Id, Name: item?.Name }))
            .filter((item: { Id: string; Name: string }) => Boolean(item?.Name));

          setDropdowns({
            salutation: res?.Salutation ?? [],
            league: res?.League__c ?? [],
            team: res?.Team__c ?? [],
            position: res?.Position__c ?? [],
            status: res?.Status__c ?? [],
            college: res?.College__c ?? [],
            agency: agencyOptionsFromDropdown,
            linkEntity: res?.linkEntity ?? [],
          });

          setAgencyOptions(agencyOptionsFromDropdown);

          if (initialValues?.agency) {
            const match = agencyOptionsFromDropdown.find(
              (item: { Id: string; Name: string }) =>
                item.Name.toLowerCase() === initialValues.agency!.toLowerCase(),
            );

            if (match) {
              setSelectedAgency(match);
            }
          }
        })
        .catch(() => {});
    }, [formValues.clientType, initialValues?.agency]);

    // ── Pre-populate agency, owner & linkEntity selects ────────────────────────
    useEffect(() => {
      if (initialValues?.agency) {
        const match = dropdowns.agency.find(
          (item: { Id: string; Name: string }) =>
            item.Name.toLowerCase() === initialValues.agency!.toLowerCase(),
        );

        if (match) {
          setSelectedAgency(match);
          setAgencyOptions(dropdowns.agency);
        } else {
          setSelectedAgency({ Id: initialValues.agency, Name: initialValues.agency });
          setAgencyOptions([{ Id: initialValues.agency, Name: initialValues.agency }]);
        }
      }
      if (initialValues?.owner && ownerOptions.length) {
        const match = ownerOptions.find(
          (o) =>
            o.Id === initialValues!.owner ||
            o.Name.toLowerCase() === initialValues!.owner!.toLowerCase(),
        );
        setSelectedOwner(match ?? { Id: initialValues.owner, Name: initialValues.owner });
      }
    }, [dropdowns.agency, initialValues?.agency, initialValues?.owner, ownerOptions]);

    // ── Agency search ───────────────────────────────────────────────────────────
    const handleAgencySearch = async (_e: React.SyntheticEvent, value: string) => {
      clearTimeout(waitRef.current);
      waitRef.current = setTimeout(() => {
        setAgencyLoading(true);
        const filtered = !value
          ? dropdowns.agency
          : dropdowns.agency.filter((a) => a.Name.toLowerCase().includes(value.toLowerCase()));
        setAgencyOptions(filtered);
        setAgencyLoading(false);
      }, 500);
    };

    // ── Address search helpers ──────────────────────────────────────────────────
    const parseAddressParts = (name: string) => {
      const parts = name.split(',');
      return {
        street: parts[0]?.trim() ?? '',
        city: parts[parts.length - 3]?.trim() ?? '',
        state: parts[parts.length - 2]?.trim() ?? '',
        country: parts[parts.length - 1]?.trim() ?? '',
      };
    };

    const handleBillingAddressSearch = (_e: React.SyntheticEvent, value: string) => {
      clearTimeout(billingWaitRef.current);
      if (!value) {
        setBillingAddressOptions([]);
        return;
      }
      billingWaitRef.current = setTimeout(async () => {
        try {
          setBillingAddressLoading(true);
          const res = await LocationService.getAddressSuggestion(value.toLowerCase());
          setBillingAddressOptions(
            (res ?? []).map((v: any) => ({ Id: v?.description, Name: v?.description })),
          );
        } catch {
          /* silent */
        } finally {
          setBillingAddressLoading(false);
        }
      }, 500);
    };

    const handleBillingAddressSelection = (val: any) => {
      const v = Array.isArray(val) ? val[0] : val;
      setSelectedBillingAddress(v ?? null);
      if (v) {
        const parts = parseAddressParts(v.Name);
        setFormValues((prev) => ({
          ...prev,
          billingStreet: parts.street,
          billingCity: parts.city,
          billingState: parts.state,
          billingCountry: parts.country,
        }));
      }
    };

    const handleMailingAddressSearch = (_e: React.SyntheticEvent, value: string) => {
      clearTimeout(mailingWaitRef.current);
      if (!value) {
        setMailingAddressOptions([]);
        return;
      }
      mailingWaitRef.current = setTimeout(async () => {
        try {
          setMailingAddressLoading(true);
          const res = await LocationService.getAddressSuggestion(value.toLowerCase());
          setMailingAddressOptions(
            (res ?? []).map((v: any) => ({ Id: v?.description, Name: v?.description })),
          );
        } catch {
          /* silent */
        } finally {
          setMailingAddressLoading(false);
        }
      }, 500);
    };

    const handleMailingAddressSelection = (val: any) => {
      const v = Array.isArray(val) ? val[0] : val;
      setSelectedMailingAddress(v ?? null);
      if (v) {
        const parts = parseAddressParts(v.Name);
        setFormValues((prev) => ({
          ...prev,
          mailingStreet: parts.street,
          mailingCity: parts.city,
          mailingState: parts.state,
          mailingCountry: parts.country,
        }));
      }
    };

    // ── Unified value change helper ─────────────────────────────────────────────
    const handleFieldChange = (key: keyof ClientFormValues) => (e: any) => {
      setFormValues((prev) => ({ ...prev, [key]: e.target.value }));
    };

    // ── Notify parent on every change ──────────────────────────────────────────
    useEffect(() => {
      const isValid = !!formValues.clientType && !!formValues.name && !!formValues.owner;
      onChange?.({ ...formValues }, isValid);
    }, [formValues]);

    // ── Sync profile image url to formValues ────────────────────────────────────
    useEffect(() => {
      setFormValues((prev) => ({ ...prev, profileImageUrl: avatarPreview ?? '' }));
    }, [avatarPreview]);

    const selectField = (
      key: keyof ClientFormValues,
      label: string,
      options: string[],
      required?: boolean,
    ) => (
      <FormControl sx={{ flex: 1 }} required={required}>
        <InputLabel
          size={isXs ? 'small' : 'medium'}
          sx={{ '&.Mui-focused': { color: zeplinColor.Neutral9 }, fontSize: isXs ? 14 : 16 }}
        >
          {label}
        </InputLabel>
        <Select
          size={isXs ? 'small' : 'medium'}
          value={formValues[key] || ''}
          onChange={handleFieldChange(key)}
          input={
            <OutlinedInput
              sx={{
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '1px',
                  borderColor: zeplinColor.Neutral9,
                },
              }}
              label={label}
            />
          }
          renderValue={(s) => `${String(s).slice(0, 1).toUpperCase()}${String(s).slice(1)}`}
          MenuProps={MenuProps}
          IconComponent={() => <img src='/assets/down-arrow.svg' width={24} height={24} alt='' />}
        >
          {options?.map((name) => (
            <MenuItem key={name} value={name}>
              <Radio
                checked={formValues[key] === name}
                sx={{
                  color: zeplinColor.BasicBlack,
                  '&.Mui-checked': { color: zeplinColor.BasicBlack },
                }}
              />
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );

    const textField = (
      key: keyof ClientFormValues,
      label: string,
      type: string = 'text',
      required?: boolean,
      disabled?: boolean,
    ) => (
      <CustomTextField
        style={{ flex: 1 }}
        name={key}
        text={(formValues[key] as string) || ''}
        form
        label={label}
        type={type}
        required={required}
        disable={disabled}
        onChange={handleFieldChange(key)}
      />
    );

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* ── Account Information ─────────────────────────────────────────────── */}
        <Box sx={sectionCard}>
          <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
            Account Information
          </Typography>

          {/* Profile photo */}
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px', alignItems: 'center' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '80px',
                height: '80px',
                borderRadius: '22px',
                border: !avatarPreview ? `1px dashed ${zeplinColor.Neutral9}` : 'none',
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <img
                src={avatarPreview ?? '/assets/account-name.svg'}
                alt='profile'
                width={avatarPreview ? 80 : 35}
                height={avatarPreview ? 80 : 35}
                style={{ objectFit: 'cover' }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Typography variant='body2' sx={{ color: zeplinColor.BasicBlack }}>
                  Profile photo
                </Typography>
                <Typography variant='label1' sx={{ color: zeplinColor.Neutral8 }}>
                  JPG or PNG (max 2 MB)
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: '8px' }}>
                <CustomButton
                  disabled={false}
                  onClick={openFileSelection}
                  variant='contained'
                  color='primary'
                  buttonVariant='buttonSmall'
                  style={{
                    borderRadius: { xs: '10px', xl: '14px' },
                    padding: '6px 12px',
                    '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
                  }}
                  textStyle={{ color: zeplinColor.Neutral1 }}
                  text={avatarPreview ? 'Change Photo' : 'Add Photo'}
                />
                {avatarPreview && (
                  <CustomButton
                    disabled={false}
                    variant='contained'
                    buttonVariant='buttonSmall'
                    onClick={clearSelection}
                    text='Delete Photo'
                    textStyle={{ color: zeplinColor.Error }}
                    style={{
                      backgroundColor: 'transparent',
                      borderRadius: { xs: '10px', xl: '14px' },
                      padding: '6px 12px',
                      '&.Mui-focusVisible, &:hover': { backgroundColor: zeplinColor.Neutral3 },
                    }}
                  />
                )}
              </Box>
            </Box>
            <input
              type='file'
              accept='.jpeg,.jpg,.png,image/jpeg,image/png'
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </Box>

          {/* Row 1 — Client Type (required) · Salutation · Client Name (required) */}
          <Box sx={{ display: 'flex', gap: '16px' }}>
            {selectField('clientType', 'Client Type', clientTypeOptions, true)}
            {selectField('salutation', 'Salutation', dropdowns.salutation)}
            {textField('name', 'Client Name', 'text', true)}
          </Box>

          {/* Row 2 — Link Entity · Agency (custom search) · Owner (required) */}
          <Box sx={{ display: 'flex', gap: '16px', alignItems: 'flex-end' }}>
            {/* Link Entity — multi-select autocomplete with chips */}
            <Autocomplete
              multiple
              size={isXs ? 'small' : 'medium'}
              options={dropdowns.linkEntity}
              value={dropdowns.linkEntity.filter((opt) => formValues.linkEntity.includes(opt.Id))}
              getOptionLabel={(option) => option.Name}
              isOptionEqualToValue={(option, value) => option.Id === value.Id}
              onChange={(_e, val) => {
                setFormValues((prev) => ({ ...prev, linkEntity: val.map((v) => v.Id) }));
              }}
              popupIcon={<img src='/assets/down-arrow.svg' width={24} height={24} alt='' />}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    checked={selected}
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': { color: zeplinColor.BasicBlack },
                    }}
                  />
                  {option.Name}
                </li>
              )}
              slotProps={{ paper: { sx: { borderRadius: '14px' } } }}
              renderInput={(params) => <TextField {...params} label='Link Entity' />}
              sx={{ flex: 1 }}
            />

            {/* Agency — custom search autocomplete */}
            <Box sx={{ flex: 1 }}>
              <CustomAutocomplete
                loading={agencyLoading}
                popupIcon='/assets/Search.svg'
                required={false}
                options={agencyOptions}
                getOptionLabel={(option: any) => option?.Name ?? ''}
                label='Agency'
                handleDeleteItems={() => {
                  setSelectedAgency(null);
                  setFormValues((prev) => ({ ...prev, agency: '' }));
                }}
                selectedItems={selectedAgency ? [selectedAgency] : []}
                handleSelection={(val: any) => {
                  const v = Array.isArray(val) ? val[0] : val;
                  setSelectedAgency(v ?? null);
                  setFormValues((prev) => ({ ...prev, agency: v?.Name ?? '' }));
                }}
                value={selectedAgency}
                handleFilter={handleAgencySearch}
                placeholder='Search agency'
                currentLength={agencyOptions.length}
                totalLength={agencyOptions.length}
                next={() => {}}
                multiple={false}
              />
            </Box>

            {/* Owner — assignee dropdown (required) */}
            <Box sx={{ flex: 1 }}>
              <CustomAutocomplete
                required={false}
                options={ownerOptions}
                getOptionLabel={(option: any) => option?.Name ?? ''}
                label='Owner *'
                handleDeleteItems={() => {
                  setSelectedOwner(null);
                  setFormValues((prev) => ({ ...prev, owner: '' }));
                }}
                selectedItems={selectedOwner ? [selectedOwner] : []}
                handleSelection={(val: any) => {
                  const v = Array.isArray(val) ? val[0] : val;
                  setSelectedOwner(v ?? null);
                  setFormValues((prev) => ({
                    ...prev,
                    owner: v?.Id ?? '',
                  }));
                }}
                value={selectedOwner}
                handleFilter={() => {}}
                placeholder='Search owner'
                currentLength={ownerOptions.length}
                totalLength={ownerOptions.length}
                next={() => {}}
                multiple={false}
              />
            </Box>
          </Box>

          {/* Row 3 — League · Team · Position */}
          <Box sx={{ display: 'flex', gap: '16px' }}>
            {selectField('league', 'League', dropdowns.league)}
            {selectField('team', 'Team', dropdowns.team)}
            {selectField('position', 'Position', dropdowns.position)}
          </Box>

          {/* Row 4 — Number · Status · College */}
          <Box sx={{ display: 'flex', gap: '16px' }}>
            {textField('number', 'Number', 'number')}
            {selectField('status', 'Status', dropdowns.status)}
            {selectField('college', 'College', dropdowns.college)}
          </Box>

          {/* Row 5 — Loan Out Company */}
          <Box sx={{ display: 'flex', gap: '16px' }}>
            {textField('loanOutCompany', 'Loan Out Company')}
            {/* empty spacers to keep 3-col grid feel */}
            <Box sx={{ flex: 1 }} />
            <Box sx={{ flex: 1 }} />
          </Box>
        </Box>

        {/* ── Contact Information ─────────────────────────────────────────────── */}
        <Box sx={sectionCard}>
          <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
            Contact Information
          </Typography>

          {/* Row 1 — Mobile · Phone · Email */}
          <Box sx={{ display: 'flex', gap: '16px' }}>
            {textField('mobile', 'Mobile', 'tel')}
            {textField('phone', 'Phone', 'tel')}
            {textField('email', 'Email', 'email')}
          </Box>

          {/* Row 2 — Personal Email */}
          <Box sx={{ display: 'flex', gap: '16px' }}>
            {textField('personalEmail', 'Personal Email', 'email')}
            <Box sx={{ flex: 1 }} />
            <Box sx={{ flex: 1 }} />
          </Box>
        </Box>

        {/* ── Mailing Address ─────────────────────────────────────────────────── */}
        <Box sx={sectionCard}>
          <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
            Mailing Address
          </Typography>

          {/* Search address */}
          <Box sx={{ display: 'flex', gap: '16px' }}>
            <Box sx={{ flex: 1 }}>
              <CustomAutocomplete
                loading={mailingAddressLoading}
                popupIcon='/assets/Search.svg'
                required={false}
                options={mailingAddressOptions}
                getOptionLabel={(option: any) => option?.Name ?? ''}
                label='Search Address'
                handleDeleteItems={() => {
                  setSelectedMailingAddress(null);
                  setFormValues((prev) => ({
                    ...prev,
                    mailingStreet: '',
                    mailingCity: '',
                    mailingState: '',
                    mailingCountry: '',
                  }));
                }}
                selectedItems={selectedMailingAddress ? [selectedMailingAddress] : []}
                handleSelection={handleMailingAddressSelection}
                value={selectedMailingAddress}
                handleFilter={handleMailingAddressSearch}
                placeholder='Search address'
                currentLength={mailingAddressOptions.length}
                totalLength={mailingAddressOptions.length}
                next={() => {}}
                multiple={false}
              />
            </Box>
            {textField('mailingStreet', 'Street')}
            {textField('mailingCity', 'City')}
          </Box>

          {/* City · State / Province · Zip / Postal Code */}
          <Box sx={{ display: 'flex', gap: '16px' }}>
            {textField('mailingState', 'State / Province')}
            {textField('mailingZip', 'Zip / Postal Code')}
            {textField('mailingCountry', 'Country')}
          </Box>
        </Box>

        {/* ── Billing Address ─────────────────────────────────────────────────── */}
        <Box sx={sectionCard}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
              Billing Address
            </Typography>
          </Box>
          <FormControlLabel
            sx={{ margin: 0 }}
            control={
              <Checkbox
                checked={sameAsMailing}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setSameAsMailing(checked);
                  if (checked) {
                    setFormValues((prev) => ({
                      ...prev,
                      billingStreet: prev.mailingStreet,
                      billingCity: prev.mailingCity,
                      billingState: prev.mailingState,
                      billingZip: prev.mailingZip,
                      billingCountry: prev.mailingCountry,
                    }));
                    setSelectedBillingAddress(selectedMailingAddress);
                  } else {
                    setFormValues((prev) => ({
                      ...prev,
                      billingStreet: '',
                      billingCity: '',
                      billingState: '',
                      billingZip: '',
                      billingCountry: '',
                    }));
                    setSelectedBillingAddress(null);
                  }
                }}
                sx={{
                  padding: '0 8px 0 0',
                  '&.Mui-checked': { color: zeplinColor.BasicBlack },
                }}
              />
            }
            label={
              <Typography variant='responsiveBody2' color={zeplinColor.BasicBlack}>
                My billing address is same as my mailing address
              </Typography>
            }
          />
          {/* Search address */}
          <Box sx={{ display: 'flex', gap: '16px' }}>
            <Box sx={{ flex: 1 }}>
              <CustomAutocomplete
                loading={billingAddressLoading}
                popupIcon='/assets/Search.svg'
                required={false}
                options={billingAddressOptions}
                getOptionLabel={(option: any) => option?.Name ?? ''}
                label='Search Address'
                disabled={sameAsMailing}
                handleDeleteItems={() => {
                  setSelectedBillingAddress(null);
                  setSameAsMailing(false);
                  setFormValues((prev) => ({
                    ...prev,
                    billingStreet: '',
                    billingCity: '',
                    billingState: '',
                    billingCountry: '',
                  }));
                }}
                selectedItems={selectedBillingAddress ? [selectedBillingAddress] : []}
                handleSelection={(val: any) => {
                  setSameAsMailing(false);
                  handleBillingAddressSelection(val);
                }}
                value={selectedBillingAddress}
                handleFilter={handleBillingAddressSearch}
                placeholder='Search address'
                currentLength={billingAddressOptions.length}
                totalLength={billingAddressOptions.length}
                next={() => {}}
                multiple={false}
              />
            </Box>
            {textField('billingStreet', 'Street', 'text', false, sameAsMailing)}
            {textField('billingCity', 'City', 'text', false, sameAsMailing)}
          </Box>

          {/* City · State / Province · Zip / Postal Code */}
          <Box sx={{ display: 'flex', gap: '16px' }}>
            {textField('billingState', 'State / Province', 'text', false, sameAsMailing)}
            {textField('billingZip', 'Zip / Postal Code', 'text', false, sameAsMailing)}
            {textField('billingCountry', 'Country', 'text', false, sameAsMailing)}
          </Box>
        </Box>

        {/* ── Bio ─────────────────────────────────────────────────────────────── */}
        <Box sx={sectionCard}>
          <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
            Bio
          </Typography>
          <CustomTextField
            style={{ width: '100%' }}
            name='bio'
            text={formValues.bio || ''}
            form
            label=''
            type='text'
            multiline
            rows={5}
            placeHolder='Add a bio…'
            onChange={handleFieldChange('bio')}
          />
        </Box>
      </Box>
    );
  },
);

ClientForm.displayName = 'ClientForm';

export default ClientForm;
export type { ClientFormValues as ClientFormValuesType };
